import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Custom pointer-event drag hook for the sidebar tree.
 *
 * Replaces @dnd-kit to avoid the SortableContext.itemsHaveChanged →
 * measureDroppableContainers → setState infinite loop on WebView2/Windows.
 *
 * Drop-target detection uses document.elementFromPoint + data-drag-id attributes
 * instead of a rect-measurement registry, so there is no internal measurement
 * system that can loop.
 */

export function activeKindFrom(id) {
  if (!id) return null;
  if (id.startsWith('collection-')) return 'collection';
  if (id.startsWith('folder-')) return 'folder';
  if (id.startsWith('request-')) return 'request';
  if (id.startsWith('schema-')) return 'schema';
  if (id.startsWith('document-')) return 'document';
  return null;
}

function overKindFrom(id) {
  return activeKindFrom(id);
}

/**
 * Derive the drop position from the pointer-Y within the target row's rect.
 *  - request / collection rows: top half → 'before', bottom half → 'after'
 *    (collection rows are forced to 'inside' by the caller).
 *  - folder / document rows: top 25% → 'before', middle 50% → 'inside',
 *    bottom 25% → 'after'. Documents earn the 'inside' zone because they can
 *    hold subpages (only DocumentsSidebar ever emits `document-` ids — the
 *    collection/request/schema trees are untouched by this branch).
 */
export function computePosition(overId, rect, y) {
  if (!rect) return 'before';
  const ratio = (y - rect.top) / Math.max(rect.height, 1);
  const kind = overKindFrom(overId);
  if (kind === 'folder' || kind === 'document') {
    if (ratio <= 0.25) return 'before';
    if (ratio >= 0.75) return 'after';
    return 'inside';
  }
  return ratio < 0.5 ? 'before' : 'after';
}

/**
 * Hit-test the point and return the nearest drop target as { id, position }.
 * Returns null when there's no valid target (or the target is the dragged item).
 */
/**
 * Swallow the `click` the browser fires after a drag is dropped (card b9799baf).
 * The dragged row holds pointer capture, so the release clicks IT: dropping a
 * request also opened it in a tab. The click follows pointerup in the same
 * task, so the listener is gone again before anything else can be clicked.
 */
export function swallowNextClick() {
  const swallow = (ev) => {
    ev.stopPropagation();
    ev.preventDefault();
  };
  window.addEventListener('click', swallow, { capture: true, once: true });
  setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
}

function findDragTargetAt(x, y, overlayEl, activeId) {
  // Make the overlay transparent for the hit-test so it doesn't occlude targets.
  // The overlay already has pointerEvents:none as a permanent style, but we guard
  // anyway in case something changes it.
  const prev = overlayEl?.style.pointerEvents;
  if (overlayEl && prev !== 'none') overlayEl.style.pointerEvents = 'none';

  const el = document.elementFromPoint(x, y);

  if (overlayEl && prev !== 'none') overlayEl.style.pointerEvents = prev ?? '';

  if (!el) return null;
  const target = el.closest('[data-drag-id]');
  const id = target?.getAttribute('data-drag-id') ?? null;
  // Never report the dragged item itself as a drop target.
  if (!id || id === activeId) return null;
  const position = computePosition(id, target.getBoundingClientRect(), y);
  return { id, position };
}

export function useSidebarDrag({
  disabled = false,
  onDragStart,
  onDragMove,
  onDragEnd,
  onDragCancel,
} = {}) {
  // pendingRef: set on pointerdown, before 8px activation threshold. Using a ref
  // (not state) avoids a render on every pointerdown.
  const pendingRef = useRef(null);

  // dragRef: source of truth for event handlers (refs don't re-register effects).
  const dragRef = useRef(null);

  // dragState: mirrors dragRef to drive React renders (overlay position, ghost effect).
  const [dragState, setDragState] = useState(null);

  // overlayRef: caller attaches this to the floating chip div.
  const overlayRef = useRef(null);

  // cbRef: keeps callbacks stable so the effect's empty dep-array stays valid.
  const cbRef = useRef({ onDragStart, onDragMove, onDragEnd, onDragCancel });
  useEffect(() => {
    cbRef.current = { onDragStart, onDragMove, onDragEnd, onDragCancel };
  });

  // Cancel any active drag when the hook is disabled externally (e.g. search opens).
  useEffect(() => {
    if (disabled && dragRef.current) {
      pendingRef.current = null;
      dragRef.current = null;
      setDragState(null);
      document.body.style.cursor = '';
      cbRef.current.onDragCancel?.();
    }
  }, [disabled]);

  useEffect(() => {
    const handleMove = (e) => {
      const p = pendingRef.current;
      if (!p || e.pointerId !== p.pointerId) return;

      if (dragRef.current) {
        // ── Active drag: update overlay position + detect new over-target ──
        const kind = activeKindFrom(dragRef.current.activeId);
        let target = findDragTargetAt(e.clientX, e.clientY, overlayRef.current, dragRef.current.activeId);

        // Collection drags may only land on other collections.
        if (kind === 'collection' && target && !target.id.startsWith('collection-')) {
          target = null;
        }
        const overId = target?.id ?? null;
        // A request/folder dropped on a collection row means "into collection root".
        // A collection dropped on a collection row is a reorder (keep before/after).
        let position = target ? target.position : null;
        if (target && overId.startsWith('collection-') && kind !== 'collection') {
          position = 'inside';
        }

        const prevOverId = dragRef.current.overId;
        const prevPosition = dragRef.current.position;
        dragRef.current = { ...dragRef.current, overId, position, x: e.clientX, y: e.clientY };
        setDragState({ ...dragRef.current });

        if (overId !== prevOverId || position !== prevPosition) {
          cbRef.current.onDragMove?.(dragRef.current.activeId, overId, position);
        }
      } else {
        // ── Pending: check 8 px activation threshold ──
        const dx = e.clientX - p.startX;
        const dy = e.clientY - p.startY;
        if (Math.sqrt(dx * dx + dy * dy) >= 8) {
          let target = findDragTargetAt(e.clientX, e.clientY, overlayRef.current, p.activeId);
          const kind = activeKindFrom(p.activeId);
          if (kind === 'collection' && target && !target.id.startsWith('collection-')) {
            target = null;
          }
          const safeOverId = target?.id ?? null;
          let safePosition = target ? target.position : null;
          if (target && safeOverId.startsWith('collection-') && kind !== 'collection') {
            safePosition = 'inside';
          }

          dragRef.current = {
            activeId: p.activeId,
            overId: safeOverId,
            position: safePosition,
            x: e.clientX,
            y: e.clientY,
          };
          setDragState({ ...dragRef.current });
          document.body.style.cursor = 'grabbing';
          cbRef.current.onDragStart?.(p.activeId);
          // Notify the initial over-target immediately so the preview starts updating.
          if (safeOverId) {
            cbRef.current.onDragMove?.(p.activeId, safeOverId, safePosition);
          }
        }
      }
    };

    const handleUp = (e) => {
      const p = pendingRef.current;
      if (!p || e.pointerId !== p.pointerId) return;
      const d = dragRef.current;
      pendingRef.current = null;
      dragRef.current = null;
      if (d) {
        setDragState(null);
        document.body.style.cursor = '';
        swallowNextClick();
        cbRef.current.onDragEnd?.(d.activeId, d.overId, d.position ?? null);
      }
    };

    const handleCancel = (e) => {
      const p = pendingRef.current;
      if (!p) return;
      if (e.pointerId !== undefined && e.pointerId !== p.pointerId) return;
      const wasDragging = !!dragRef.current;
      pendingRef.current = null;
      dragRef.current = null;
      if (wasDragging) {
        setDragState(null);
        document.body.style.cursor = '';
      }
      cbRef.current.onDragCancel?.();
    };

    const handleKey = (e) => {
      if (e.key === 'Escape') handleCancel(e);
    };

    // Capture phase ensures child stopPropagation() (bubble phase) can't block us.
    window.addEventListener('pointermove', handleMove, { capture: true });
    window.addEventListener('pointerup', handleUp, { capture: true });
    window.addEventListener('pointercancel', handleCancel, { capture: true });
    window.addEventListener('keydown', handleKey);

    return () => {
      window.removeEventListener('pointermove', handleMove, { capture: true });
      window.removeEventListener('pointerup', handleUp, { capture: true });
      window.removeEventListener('pointercancel', handleCancel, { capture: true });
      window.removeEventListener('keydown', handleKey);
    };
  }, []); // empty — all state accessed via refs

  /**
   * Returns { onPointerDown } props to spread onto a draggable element.
   * Pass opts.disabled = true to make a specific item non-draggable without
   * disabling the whole hook.
   */
  const getHandleProps = useCallback((id, opts = {}) => {
    if (disabled || opts.disabled) return {};
    return {
      onPointerDown: (e) => {
        if (e.button !== 0) return; // left-button / primary touch only
        // `preventDefault` below stops text selection during a drag — and with it
        // the FOCUS change a mousedown normally causes. So an inline rename open
        // on another row never blurred, never committed, and stayed open until
        // Enter (owner, 18.09.2026: "if i do rename with ctrl + e and i click away
        // it would not turn off the inline editing"). Blur it by hand first. The
        // editor stops its own pointerdown from reaching here, so a click INSIDE
        // the input is unaffected.
        const focused = document.activeElement;
        if (focused && focused !== document.body && !e.currentTarget.contains(focused)) {
          focused.blur?.();
        }
        e.preventDefault(); // prevent text selection during drag
        pendingRef.current = {
          pointerId: e.pointerId,
          activeId: id,
          startX: e.clientX,
          startY: e.clientY,
        };
        // Capture keeps pointermove/pointerup routing to this element even when
        // the cursor leaves it before the 8 px threshold — critical on fast moves.
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
      },
    };
  }, [disabled]);

  return {
    activeDragId: dragState?.activeId ?? null,
    overId: dragState?.overId ?? null,
    dropPosition: dragState?.position ?? null,
    dragX: dragState?.x ?? 0,
    dragY: dragState?.y ?? 0,
    isDragging: dragState !== null,
    overlayRef,
    getHandleProps,
  };
}
